export enum RegressionSetFrequencyType {
  WEEKLY = "weekly",
  MONTHLY = "monthly",
}

export enum RegressionSetFrequencyDayOfWeek {
  SUNDAY = "sunday",
  MONDAY = "monday",
  TUESDAY = "tuesday",
  WEDNESDAY = "wednesday",
  THURSDAY = "thursday",
  FRIDAY = "friday",
  SATURDAY = "saturday",
}

export interface RegressionSetFrequency {
  type: RegressionSetFrequencyType;
  dayOfWeek?: RegressionSetFrequencyDayOfWeek;
  dayOfMonth?: number;
}

export interface RegressionSet {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  items: RegressionSetItem[];
  frequency?: RegressionSetFrequency;
}

export interface RegressionSetItem {
  id: string;
  regressionSetId: string;
  question: string;
  expectedAnswer: string;
  expectedDecision?: RegressionDecision | null;
  releaseConversationId?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRegressionSetPayload {
  name: string;
  description?: string;
  simulatorId?: number;
}

export interface UpdateRegressionSetPayload {
  name?: string;
  description?: string;
  frequency?: RegressionSetFrequency;
}

export interface CreateRegressionSetItemPayload {
  question?: string;
  expectedAnswer?: string;
  expectedDecision?: RegressionDecision;
  releaseConversationId?: number;
}

export interface UpdateRegressionSetItemPayload {
  question?: string;
  expectedAnswer?: string;
}

export interface RunRegressionTestBySetPayload {
  regressionSetId: string;
  releaseId: number;
  auiParameter?: boolean;
}
export interface RunRegressionTestByTargetReleasePayload {
  releaseToTestId: number;
  releaseToTestAgainstId?: number;
  numberOfConversations?: number;
}

export interface CreateRegressionSetFromReleasePayload {
  name: string;
  description?: string;
  releaseId: number;
}

export interface CreateRegressionSetFromLastFeedbacksPayload {
  name: string;
  description?: string;
  limit?: number;
}

export interface CreateRegressionSetFromConversationsPayload {
  name: string;
  description?: string;
  releaseIds?: number[];
  topicIds?: number[];
  simulatorId?: number;
}

export interface RegressionSetItemWithConversation extends RegressionSetItem {}

export interface RegressionSetFromConversationsResponse {
  id: string;
  name: string;
  description?: string;
  items: RegressionSetItemWithConversation[];
  createdAt: string;
}

export interface RegressionTestResult {
  itemId: string;
  question: string;
  expectedAnswer: string;
  aiResponse: string;
  similarity: number;
  passed: boolean;
  error?: string;
}

export interface RegressionTestBySetResponse {
  regressionTestId: string;
  regressionSetId?: string;
  releaseId?: number;
  results: RegressionTestResult[];
  summary?: {
    total: number;
    passed: number;
    failed: number;
    averageSimilarity: number;
  };
}

export interface RegressionSetBySimulatorId {
  id: string;
  name: string;
  description: string;
  simulatorId: number;
  frequency: object;
  nextRun: string;
  regressionTestCount: number;
  numberOfConversations: number;
  mostRecentTest: {
    avgAccuracy: number | null;
    runTime: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface RegressionTestBySetId {
  id: string;
  createdAt: string;
  runMethod: "manual" | "auto" | "aui";
  avgAccuracy: number | null;
  numberOfResults: number;
  passedQuestions?: number;
  failedQuestions?: number;
}

export type RegressionDecision = "escalate" | "none";

export interface RegressionTestResultDetail {
  id: string;
  regressionTestId: string;
  regressionSetItemId: string;
  createdAt: string;
  updatedAt: string;
  releaseConversationId: string | null;
  userCommentId: string | null;
  agentCommentId: string | null;
  aiResponse: string;
  decision: RegressionDecision | null;
  similarity: number;
  regressionTest: {
    id: string;
    createdAt: string;
  };
  regressionSetItem: {
    id: string;
    question: string;
    expectedAnswer: string;
    expectedDecision: RegressionDecision | null;
    releaseConversation: {
      id: string;
      releaseComments: Array<{
        id: string;
        comment: string;
        isAgent: boolean;
        createdAt: string;
      }>;
    } | null;
  };
  releaseConversation: any | null;
  userComment: any | null;
  agentComment: any | null;
}

export interface AllRegressionTests {
  id: string;
  createdAt: string;
  regressionSetId?: string;
  runMethod?: "manual" | "auto" | "aui";
  release: {
    id: number;
  };
  releaseToTestAgainst: {
    id: number;
  };
  results: Array<{
    id: string;
    similarity: number;
    aiResponse: string;
    regressionSetItem: {
      id: string;
      question: string;
      expectedAnswer: string;
    } | null;
  }>;
}

export interface AuiRegressionTestJudgeDistinctPair {
  manualRegressionTestId: string;
  auiRegressionTestId: string;
  regressionSetName: string;
  createdAt: string;
}

export type JudgeVerdict = string;

export interface JudgeDimensionScores {
  factual_accuracy: number;
  content_completeness: number;
  primary_intent_coverage: number;
  irrelevant_content: number;
  persona_tone: number;
  format_structure: number;
  total: number;
}

export interface JudgeScoresParsed {
  quack_scores: JudgeDimensionScores;
  aui_scores: JudgeDimensionScores;
}

export interface RegressionTestResultsJudgeRow {
  tenantId: string;
  id: string;
  judgeProcessId: string;
  createdAt: string;
  regressionSetId: string;
  regressionSetName: string;
  regressionSetSimulatorId: number | null;
  regressionSetItemId: string;
  regressionSetItemQuestion: string | null;
  regressionSetItemExpectedAnswer: string | null;
  regressionSetItemExpectedDecision: string;
  regressionSetItemReleaseConversationId: number | null;
  manualRegressionTestId: string;
  manualRegressionTestReleaseId: number;
  manualRegressionTestResultId: string;
  manualRegressionTestResultAiResponse: string;
  manualRegressionTestResultDecision: string;
  manualRegressionTestResultSimilarity: number;
  auiRegressionTestId: string;
  auiRegressionTestReleaseId: number;
  auiRegressionTestResultId: string;
  auiRegressionTestResultAiResponse: string;
  auiRegressionTestResultDecision: string;
  auiRegressionTestResultSimilarity: number;
  judgeVerdict: JudgeVerdict;
  judgeReasoning: string;
  judgeScores?: string | null;
  regressionSetItemExpectedAnswerEssence?: string | null;
  manualRegressionTestResultAiResponseEssence?: string | null;
  manualRegressionTestResultAiResponseEssenceSimilarity?: number;
  auiRegressionTestResultAiResponseEssence?: string | null;
  auiRegressionTestResultAiResponseEssenceSimilarity?: number;
  essenceJudgeVerdict?: string;
  essenceJudgeReasoning?: string | null;
  essenceJudgeScores?: string | null;
}
